// The host self-check (`/host-check`, views/HostCheck.tsx): phase 0 of `design-docs/Windows app — design.md`, run in
// the window it asks about. Each automatic check reads what this browser or shell can do; the passkey trial makes a
// throwaway passkey for this site and asks it for PRF output twice, as signing up does (client/browser/webauthn.ts),
// so a Windows Hello, phone or security-key answer can be read off the page. Nothing here is sent anywhere: the
// report is text the tester copies.
import { prfSalt, random } from '@writing-suite/client/shared/crypto.ts';
import { host, policyViolations } from './host.ts';

export type CheckStatus = 'pass' | 'fail' | 'info';

export interface CheckResult {
  id: string;
  label: string;
  status: CheckStatus;
  detail: string;
}

const result = (id: string, label: string, status: CheckStatus, detail: string): CheckResult => ({ id, label, status, detail });

const MARKER_DB = 'fiverooms-host-check';

/** Reads the last visit's marker and writes this one's, so a restart shows whether IndexedDB kept it. */
async function visitMarker(now: number): Promise<number | null> {
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(MARKER_DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore('marker');
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  try {
    const tx = db.transaction('marker', 'readwrite');
    const store = tx.objectStore('marker');
    const previous = await new Promise<unknown>((resolve, reject) => {
      const get = store.get('visit');
      get.onsuccess = () => resolve(get.result);
      get.onerror = () => reject(get.error);
    });
    store.put(now, 'visit');
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    return typeof previous === 'number' ? previous : null;
  } finally {
    db.close();
  }
}

/** The checks that need no one at the keyboard. */
export async function automaticChecks(now = Date.now()): Promise<CheckResult[]> {
  const out: CheckResult[] = [];
  const h = await host();
  out.push(
    h.info
      ? result('host', 'Native shell', 'pass', `${h.info.shell} ${h.info.version}, protocol ${h.info.protocol}, lends ${h.info.capabilities.join(', ') || 'nothing'}`)
      : result('host', 'Native shell', 'info', 'none: this is a browser (or the shell grants this page no IPC)'),
  );
  out.push(
    result('origin', 'Origin', window.isSecureContext ? 'pass' : 'fail', `${location.origin}${window.isSecureContext ? '' : ' (not a secure context: passkeys and the service worker need one)'}`),
  );

  const sw = 'serviceWorker' in navigator ? navigator.serviceWorker : null;
  const registration = sw ? await sw.getRegistration('/').catch(() => undefined) : undefined;
  out.push(
    !sw
      ? result('serviceWorker', 'Service worker (works offline)', 'fail', 'not available')
      : result(
          'serviceWorker',
          'Service worker (works offline)',
          sw.controller ? 'pass' : registration ? 'info' : 'fail',
          sw.controller ? 'controls this page' : registration ? 'registered; reload once and it controls the page' : 'not registered',
        ),
  );

  const persisted = navigator.storage?.persisted ? await navigator.storage.persisted().catch(() => false) : false;
  const estimate = navigator.storage?.estimate ? await navigator.storage.estimate().catch(() => null) : null;
  const gb = (n: number | undefined) => (n === undefined ? '?' : `${(n / 1e9).toFixed(2)} GB`);
  out.push(result('storage', 'Persistent storage', persisted ? 'pass' : 'info', `${persisted ? 'persisted' : 'not persisted (may be evicted)'}; ${gb(estimate?.usage)} used of ${gb(estimate?.quota)}`));

  try {
    const previous = await visitMarker(now);
    out.push(
      result(
        'indexedDB',
        'IndexedDB kept across restarts',
        previous ? 'pass' : 'info',
        previous ? `last visit ${new Date(previous).toLocaleString()}` : 'first visit recorded: quit, reopen and check again',
      ),
    );
  } catch (e) {
    out.push(result('indexedDB', 'IndexedDB kept across restarts', 'fail', e instanceof Error ? e.message : String(e)));
  }

  const tt = (window as unknown as { trustedTypes?: { createPolicy(name: string, rules: object): unknown } }).trustedTypes;
  let enforced = false;
  try {
    tt?.createPolicy(`host-check-${Math.random().toString(36).slice(2)}`, {});
  } catch {
    enforced = true; // the CSP names the only policies allowed, so an unnamed one is refused
  }
  out.push(result('trustedTypes', 'Trusted Types enforced', enforced ? 'pass' : 'info', enforced ? 'the page’s policy is in force' : tt ? 'a new policy was allowed: the CSP isn’t applied here' : 'not supported'));

  const violations = policyViolations();
  out.push(
    result(
      'policy',
      'Content Security Policy violations',
      violations.length ? 'fail' : 'pass',
      violations.length ? violations.slice(-5).map((v) => `${v.directive} ← ${v.blocked}`).join('; ') : 'none since the page loaded',
    ),
  );

  const pkc = typeof PublicKeyCredential === 'function' ? PublicKeyCredential : null;
  if (!pkc) out.push(result('webauthn', 'Passkeys', 'fail', 'WebAuthn isn’t available here'));
  else {
    const platform = await pkc.isUserVerifyingPlatformAuthenticatorAvailable().catch(() => false);
    out.push(result('platformAuthenticator', 'Built-in authenticator (Windows Hello, Touch ID)', platform ? 'pass' : 'fail', platform ? 'available' : 'not available'));
    const getCaps = (pkc as unknown as { getClientCapabilities?: () => Promise<Record<string, boolean>> }).getClientCapabilities;
    if (getCaps) {
      const caps = await getCaps.call(pkc).catch(() => null);
      const prf = caps?.['extension:prf'];
      out.push(result('prfCapability', 'Browser says it supports PRF', prf ? 'pass' : 'info', caps ? `extension:prf = ${prf ?? 'not listed'}; hybrid = ${caps.hybridTransport ?? '?'}` : 'getClientCapabilities failed'));
    } else out.push(result('prfCapability', 'Browser says it supports PRF', 'info', 'getClientCapabilities isn’t available (the passkey trial is the real test)'));
  }
  return out;
}

export interface PasskeyTrial {
  steps: CheckResult[];
}

type PrfOut = { enabled?: boolean; results?: { first?: ArrayBuffer | ArrayBufferView } };

const prfOf = (cred: PublicKeyCredential) => (cred.getClientExtensionResults() as { prf?: PrfOut }).prf;
const prfLength = (prf: PrfOut | undefined) => {
  const first = prf?.results?.first;
  return first ? first.byteLength : 0;
};
const prfBytes = (prf: PrfOut | undefined) => {
  const first = prf?.results?.first;
  if (!first) return null;
  return first instanceof ArrayBuffer ? new Uint8Array(first) : new Uint8Array(first.buffer, first.byteOffset, first.byteLength);
};
const failure = (e: unknown) => (e instanceof DOMException ? `${e.name}: ${e.message}` : e instanceof Error ? e.message : String(e));

/**
 * Makes a passkey named "Five Rooms test (delete me)" for this site and asks it for PRF output twice with the app's
 * own salt. Run it from a click. It leaves the passkey on the authenticator (Windows: Settings ▸ Accounts ▸ Passkeys).
 */
export async function passkeyTrial(): Promise<PasskeyTrial> {
  const steps: CheckResult[] = [];
  const salt = await prfSalt();
  let cred: PublicKeyCredential;
  try {
    cred = (await navigator.credentials.create({
      publicKey: {
        challenge: random(32),
        rp: { id: location.hostname, name: 'Five Rooms test' },
        // Windows lists a passkey by its name, and offers it at sign-in like any other, so the name says what it is
        user: { id: random(16), name: 'five-rooms-test (delete me)', displayName: 'Five Rooms test (delete me)' },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        authenticatorSelection: { residentKey: 'required', userVerification: 'required' },
        extensions: { credProps: true, prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential;
  } catch (e) {
    steps.push(result('create', 'Make a passkey', 'fail', failure(e)));
    return { steps };
  }
  const made = prfOf(cred);
  const transports = (cred.response as AuthenticatorAttestationResponse).getTransports?.() ?? [];
  steps.push(result('create', 'Make a passkey', 'pass', `${cred.authenticatorAttachment ?? 'unknown attachment'}; transports ${transports.join(', ') || 'none'}`));
  steps.push(
    result(
      'cleanUp',
      'Delete it afterwards',
      'info',
      '“five-rooms-test (delete me)” is offered whenever you sign in to Five Rooms until it’s deleted, and signing in with it fails (on Windows: Settings ▸ Accounts ▸ Passkeys)',
    ),
  );
  steps.push(
    result(
      'prfAtCreate',
      'PRF when made',
      prfLength(made) === 32 ? 'pass' : 'info',
      made ? `enabled = ${made.enabled ?? 'not said'}; output ${prfLength(made) ? `${prfLength(made)} bytes` : 'none'}` : 'no prf result (the browser ignored the extension)',
    ),
  );

  const outputs: (Uint8Array | null)[] = [];
  for (const n of [1, 2]) {
    try {
      const got = (await navigator.credentials.get({
        publicKey: {
          challenge: random(32),
          rpId: location.hostname,
          allowCredentials: [{ type: 'public-key', id: cred.rawId }],
          userVerification: 'required',
          extensions: { prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs,
        },
      })) as PublicKeyCredential;
      const prf = prfOf(got);
      outputs.push(prfBytes(prf));
      steps.push(result(`prfAtGet${n}`, `PRF when signing (${n} of 2)`, prfLength(prf) === 32 ? 'pass' : 'fail', prfLength(prf) ? `${prfLength(prf)} bytes` : 'none'));
    } catch (e) {
      steps.push(result(`prfAtGet${n}`, `PRF when signing (${n} of 2)`, 'fail', failure(e)));
      return { steps };
    }
  }
  const [a, b] = outputs;
  if (a && b) {
    const same = a.length === b.length && a.every((x, i) => x === b[i]);
    steps.push(result('prfStable', 'The same output both times', same ? 'pass' : 'fail', same ? 'yes: the vault could be opened with it' : 'no: the output changed'));
  }
  return { steps };
}

/** The report the tester copies. */
export function report(sections: { title: string; results: CheckResult[] }[], answers: Record<string, string>): string {
  const lines = [`Five Rooms host check — ${new Date().toISOString()}`, `User agent: ${navigator.userAgent}`, ''];
  for (const s of sections) {
    if (!s.results.length) continue;
    lines.push(`## ${s.title}`);
    for (const r of s.results) lines.push(`- [${r.status}] ${r.label}: ${r.detail}`);
    lines.push('');
  }
  const answered = Object.entries(answers);
  if (answered.length) {
    lines.push('## By hand');
    for (const [q, a] of answered) lines.push(`- ${q}: ${a}`);
  }
  return lines.join('\n');
}
